import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEur, fmtNum, fmtMwhc, fmtDate, SECTOR_META } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WelcomeBanner, hasWelcome, clearWelcome } from "@/components/WelcomeBanner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { FilePlus2, FolderKanban, Euro, Gauge, ArrowRight, Loader2, BookOpen, Building2, Factory, Home, Tractor, Flame } from "lucide-react";
import { PartnerOverview } from "@/components/PartnerOverview";
import { DueTasksCard } from "@/components/crm/DueTasksCard";

const SECTOR_ICON = { BAR: Home, BAT: Building2, IND: Factory, AGRI: Tractor, RES: Flame };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [welcome, setWelcome] = useState(hasWelcome);

  useEffect(clearWelcome, []);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({ total_projects: 0 }));
  }, []);

  if (!stats) {
    return (
      <div className="grid place-items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-genis" />
      </div>
    );
  }

  const global = stats.scope === "all";
  const cards = [
    { label: "Projets", value: fmtNum(stats.total_projects), icon: FolderKanban, sub: global ? "tous espaces confondus" : "simulations enregistrées" },
    { label: "Volume cumulé", value: fmtMwhc(stats.total_kwhc), icon: Gauge, sub: `${fmtNum(stats.total_kwhc)} kWh cumac` },
    { label: "Prime estimée totale", value: fmtEur(stats.total_prime), icon: Euro, sub: "toutes simulations" },
  ];

  const pieData = (stats.sector_repartition || []).filter((s) => s.count > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {welcome && (
        <WelcomeBanner
          name={user?.name?.split(" ")[0]}
          space={user?.role === "admin" ? "Administrateur" : user?.partner ? `Espace ${user.partner.name}` : "Espace Genis Energie"}
          stats={stats}
          onClose={() => setWelcome(false)}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-genis">Tableau de bord</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">
            Bonjour {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted-foreground mt-1" data-testid="dashboard-scope-text">{global ? "Vue globale : dossiers de Genis Energie et de tous les partenaires." : "Pilotez vos simulations de primes CEE."}</p>
        </div>
        <Button onClick={() => navigate("/simulation/new")} className="bg-genis hover:bg-genis-dark text-white h-11" data-testid="dashboard-new-simulation-btn">
          <FilePlus2 className="h-4 w-4 mr-1.5" /> Nouvelle simulation
        </Button>
      </div>

      {user?.role === "admin" && <DueTasksCard onOpenClient={(id) => navigate(`/crm?client=${id}`)} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {cards.map((c, i) => (
          <Card key={i} className="p-6 border-border animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</span>
              <div className="h-9 w-9 grid place-items-center rounded-lg bg-genis/10 text-genis">
                <c.icon className="h-4.5 w-4.5" />
              </div>
            </div>
            <div className="mt-4 metric text-3xl font-bold text-genis-deep dark:text-white">{c.value}</div>
            <div className="mt-1 text-sm text-muted-foreground">{c.sub}</div>
          </Card>
        ))}
      </div>

      {global && stats.by_partner && <PartnerOverview partners={stats.by_partner} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-6 lg:col-span-1">
          <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Répartition par secteur</h3>
          <p className="text-sm text-muted-foreground mb-2">Opérations par catégorie</p>
          {pieData.length === 0 ? (
            <div className="h-56 grid place-items-center text-sm text-muted-foreground">Aucune donnée pour le moment</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={pieData} dataKey="count" nameKey="label" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {pieData.map((e) => (
                    <Cell key={e.sector} fill={SECTOR_META[e.sector].color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Projets récents</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/projects")} className="text-genis" data-testid="view-all-projects-btn">
              Tout voir <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          {(!stats.recent_projects || stats.recent_projects.length === 0) ? (
            <div className="py-10 text-center">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-genis/10 text-genis mb-3">
                <BookOpen className="h-7 w-7" />
              </div>
              <p className="text-muted-foreground">Aucun projet. Créez votre première simulation.</p>
              <Button onClick={() => navigate("/simulation/new")} className="mt-4 bg-genis hover:bg-genis-dark text-white" data-testid="empty-new-btn">
                <FilePlus2 className="h-4 w-4 mr-1.5" /> Démarrer
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {stats.recent_projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(p.status === "brouillon" && p.user_id === user?.id ? `/simulation/new?id=${p.id}` : `/projects/${p.id}`)}
                  className="w-full flex items-center justify-between py-3 text-left hover:bg-secondary/50 rounded-lg px-2 -mx-2 transition-colors"
                  data-testid={`recent-project-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold truncate text-genis-deep dark:text-white">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.partner_name && <span className="mr-1.5 rounded-full border border-genis/30 bg-genis/10 px-2 py-0.5 text-[10px] font-semibold text-genis" data-testid={`recent-project-partner-${p.id}`}>{p.partner_name}</span>}
                      {p.client || "—"} · Zone {p.zone} · {fmtDate(p.updated_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex gap-1">
                      {[...new Set((p.result?.operations || []).map((o) => o.sector))].map((s) => {
                        const Icon = SECTOR_ICON[s];
                        if (!Icon) return null;
                        return (
                          <span key={s} className="h-6 w-6 grid place-items-center rounded" style={{ background: `${SECTOR_META[s].color}20`, color: SECTOR_META[s].color }}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                        );
                      })}
                    </div>
                    <div className="text-right">
                      <div className="metric font-bold text-genis">{fmtEur(p.result?.total_prime)}</div>
                      <div className="text-xs text-muted-foreground metric">{fmtMwhc(p.result?.total_kwhc)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
